import { BillingType } from '@/app/orders/pharmacy/[pharmacyOrderId]/options/types'
import { baseApi } from './baseQuery'
import {
    GmCardInfo,
    GmCardResponse,
    PharmacyOrder,
    PharmacyOrderCheckoutResponse,
    PharmacyOrderInvoice,
    PharmacyOrderResponse,
} from '@/types/interfaces/pharmacy'

export const pharmacyOrderCheckoutMutationCacheKey = (pharmacyOrderId: string) =>
    `pharmacy-order-checkout-${pharmacyOrderId}`

interface CancelPharmacyOrderResponse {
    pharmacy_order_id: string
    pharmacy_order_state: string
    pharmacy_submission_id: string
    pharmacy_submission_state: string
    state: string
    can_cancel_pharmacy_order: boolean
    can_switch_pharmacy: boolean
    can_choose_new_pharmacy: boolean
}

export const pharmacyOrderApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getPharmacyOrder: builder.query<PharmacyOrder, string>({
            query: (pharmacyOrderId) => `/api/external/pharmacy_orders/${pharmacyOrderId}`,
            transformResponse: (response: PharmacyOrderResponse) => response.order,
            providesTags: (_result, _error, pharmacyOrderId) => [{ type: 'PharmacyOrder', id: pharmacyOrderId }],
        }),

        updatePharmacyOrder: builder.mutation<
            { pharmacy_order_id: string; current_payment: PharmacyOrder['current_payment'] },
            { pharmacyOrderId: string; billing_type: BillingType }
        >({
            query: ({ pharmacyOrderId, billing_type }) => ({
                url: `/api/external/pharmacy_orders/${pharmacyOrderId}`,
                method: 'PUT',
                body: { billing_type },
            }),
            invalidatesTags: (_result, _error, { pharmacyOrderId }) => [{ type: 'PharmacyOrder', id: pharmacyOrderId }],
        }),

        cancelPharmacyOrder: builder.mutation<
            CancelPharmacyOrderResponse,
            { pharmacyOrderId: string; pharmacyChange: boolean }
        >({
            query: ({ pharmacyOrderId, pharmacyChange }) => ({
                url: `/api/external/pharmacy_orders/${pharmacyOrderId}/cancel`,
                method: 'POST',
                body: { pharmacy_change: pharmacyChange },
            }),
            invalidatesTags: (_result, _error, { pharmacyOrderId }) => [
                { type: 'PharmacyOrder', id: pharmacyOrderId },
                'Order',
            ],
        }),

        // Initiates checkout whenever stripe payment is involved, checkout is finalized appropriately (based off billing_type) once user pays
        initiateCheckoutPayment: builder.mutation<
            PharmacyOrderCheckoutResponse,
            { pharmacyOrderId: string; return_url: string; payment_method_id?: string }
        >({
            query: ({ pharmacyOrderId, return_url, payment_method_id }) => ({
                url: `/api/external/pharmacy_orders/${pharmacyOrderId}/checkout`,
                method: 'POST',
                body: { return_url, payment_method_id },
            }),
            invalidatesTags: (_result, _error, { pharmacyOrderId }) => [{ type: 'PharmacyOrder', id: pharmacyOrderId }],
        }),
        //Checkout & finalize insurance orders when no clinical review fee
        checkoutPharmacyOrderWithInsurance: builder.mutation<PharmacyOrderCheckoutResponse, string>({
            query: (pharmacyOrderId) => ({
                url: `/api/external/pharmacy_orders/${pharmacyOrderId}/checkout_with_insurance`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, pharmacyOrderId) => [{ type: 'PharmacyOrder', id: pharmacyOrderId }],
        }),
        // Checkout & finalize pharmacy orders when no clinical review fee
        checkoutPharmacyOrderThroughPharmacy: builder.mutation<PharmacyOrderCheckoutResponse, string>({
            query: (pharmacyOrderId) => ({
                url: `/api/external/pharmacy_orders/${pharmacyOrderId}/checkout_for_pharmacy_delegation`,
                method: 'POST',
            }),
            invalidatesTags: (_result, _error, pharmacyOrderId) => [{ type: 'PharmacyOrder', id: pharmacyOrderId }],
        }),

        selectPharmacy: builder.mutation<
            { pharmacy_order_id: string },
            { pharmacyOrderId: string; ncpdpProviderId: string; orderItemInquiryId?: string | null }
        >({
            query: ({ pharmacyOrderId, ncpdpProviderId, orderItemInquiryId }) => ({
                url: `/api/external/pharmacy_orders/${pharmacyOrderId}/select_pharmacy`,
                method: 'POST',
                body: {
                    ncpdp_provider_id: ncpdpProviderId,
                    ...(orderItemInquiryId ? { arrive_rtpbi_inquiry_id: orderItemInquiryId } : {}),
                },
            }),
            invalidatesTags: (_result, _error, { pharmacyOrderId }) => [{ type: 'PharmacyOrder', id: pharmacyOrderId }],
        }),

        getPharmacyOrderGmCard: builder.query<GmCardInfo, string>({
            query: (pharmacyOrderId) => `/api/external/pharmacy_orders/${pharmacyOrderId}/gm_card`,
            transformResponse: (response: GmCardResponse) => response.gm_card,
            providesTags: (_result, _error, pharmacyOrderId) => [
                { type: 'PharmacyOrder', id: `${pharmacyOrderId}-gm-card` },
            ],
        }),

        getPharmacyOrderInvoice: builder.query<PharmacyOrderInvoice, string>({
            query: (pharmacyOrderId) => `/api/external/pharmacy_orders/${pharmacyOrderId}/invoice`,
            providesTags: (_result, _error, pharmacyOrderId) => [
                { type: 'PharmacyOrder', id: `${pharmacyOrderId}-invoice` },
            ],
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetPharmacyOrderQuery,
    useLazyGetPharmacyOrderQuery,
    useUpdatePharmacyOrderMutation,
    useCancelPharmacyOrderMutation,
    useSelectPharmacyMutation,
    useInitiateCheckoutPaymentMutation,
    useCheckoutPharmacyOrderWithInsuranceMutation,
    useCheckoutPharmacyOrderThroughPharmacyMutation,
    useGetPharmacyOrderGmCardQuery,
    useGetPharmacyOrderInvoiceQuery,
} = pharmacyOrderApi
